import { useState, useCallback, useMemo } from 'react';

/**
 * Manages order line items, coupon discounts, and total calculation.
 * Pure calculation — no API dependency. No tax/GST applied.
 *
 * @param {import('../types/index').OrderItem[]} initialItems
 */
export function useOrderSummary(initialItems = []) {
    const [items] = useState(initialItems);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [couponCode, setCouponCode] = useState(null);
    const [couponLabel, setCouponLabel] = useState(null);

    const subtotal = useMemo(
        () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
        [items]
    );

    const discount = Math.min(discountAmount, subtotal);
    const tax = 0; // No tax/GST applied
    const total = parseFloat((subtotal - discount).toFixed(2));

    const applyDiscount = useCallback((amount, code, label = null) => {
        setDiscountAmount(amount);
        setCouponCode(code);
        setCouponLabel(label || `Coupon "${code}" applied`);
    }, []);

    const removeDiscount = useCallback(() => {
        setDiscountAmount(0);
        setCouponCode(null);
        setCouponLabel(null);
    }, []);

    return {
        items,
        subtotal,
        tax,
        discount,
        total,
        couponCode,
        couponLabel,
        applyDiscount,
        removeDiscount,
    };
}
